import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { ReactFlow, Handle, Position, BezierEdge, SmoothStepEdge, StepEdge, useEdges } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import RiskOverviewClimateIcon from '/src/Components/RiskOverviewClimateIcon/RiskOverviewClimateIcon';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import './style.css';
import { WindowSizeContext } from '/src/App';

// Custom node components - just handles
const RiskNode = () => {
    return (
        <div style={{ width: 5, height: 35, background: 'transparent' }}>
            <Handle type="source" position={Position.Bottom} style={{ visibility: 'visible' }} />
        </div>
    );
};

const ProjectNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="target" position={Position.Top} style={{ visibility: 'visible' }} />
            <Handle type="source" position={Position.Bottom} style={{ visibility: 'visible' }} />
        </div>
    );
};

const HouseMarkerNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="target" position={Position.Top} style={{ visibility: 'visible' }} />
        </div>
    );
};

const SolvingFor = ({
    projectsList,
    simulatorData,
    propertyData,
    currentSection
}) => {
    const { deviceType, width, height } = useContext(WindowSizeContext);
    const [houseAreasThreatened, setHouseAreasThreatened] = useState([]);
    const [filteredHouseAreasThreatened, setFilteredHouseAreasThreatened] = useState([]);
    const [filteredClimateRisks, setFilteredClimateRisks] = useState([]);
    const [showFlow, setShowFlow] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const hasRunRef = useRef(false);

    const climateRisks = propertyData?.data?.climate_risks;
    const new_hub_house_image_filename = "house-lansdcape.webp";
    const new_hub_house_image_url = CreateCloudfrontUrl(new_hub_house_image_filename);

    // Sort the projectsList by the filteredAreasIdOrderLeftToRight array
    const filteredAreasIdOrderLeftToRight = [
        '1', '2', '7', '5', '4', '8', '10', '6', '13', '9'
    ]
    const sortedProjectsList = projectsList.sort((a, b) => {
        const aIndex = filteredAreasIdOrderLeftToRight.indexOf(a.project_home_category_id);
        const bIndex = filteredAreasIdOrderLeftToRight.indexOf(b.project_home_category_id);
        return aIndex - bIndex;
    });

    useEffect(() => {
        const house_areas_threatened = propertyData?.data?.property_areas_threatened?.house_categories_with_risks;
        if (!house_areas_threatened) return;

        const areas = Array.isArray(house_areas_threatened) ? house_areas_threatened : [];
        const totalRisk = areas.reduce((sum, area) => {
            return sum + (area.rebuild_risk_weighted_likelihood_est || 0);
        }, 0);

        const updatedAreas = areas.map(area => ({
            ...area,
            risk_pct_doughnut: totalRisk ?
                ((area.rebuild_risk_weighted_likelihood_est || 0) / totalRisk) * 100 : 0
        }));

        updatedAreas.sort((a, b) => b.risk_pct_doughnut - a.risk_pct_doughnut);

        updatedAreas.forEach(area => {
            area.risk_level_adjusted = area.risk_pct_doughnut > 10 ? 'high' : area.risk_pct_doughnut > 5 ? 'medium' : 'low';
        });

        setHouseAreasThreatened(updatedAreas);
    }, [propertyData]);

    useEffect(() => {
        const projectHomeCategoryIds = new Set(projectsList.map(project => project.project_home_category_id));
        const filteredAreas = houseAreasThreatened.filter(area => projectHomeCategoryIds.has(area.id));




        setFilteredHouseAreasThreatened(filteredAreas);
    }, [houseAreasThreatened, projectsList]);

    useEffect(() => {
        // Get the climate risks that are associated with the projects
        const filteredClimateRisks = climateRisks.filter(risk => {
            return projectsList.some(project => 
                project.project_associated_climate_risks.some(projectRisk => 
                    projectRisk.risk_name === risk.risk_name
                )
            );
        });
        // console.log('Filtered Climate Risks', filteredClimateRisks);
        setFilteredClimateRisks(filteredClimateRisks);
    }, [climateRisks, projectsList]);

    // Function to get element position relative to the container
    const getElementPosition = useCallback((element) => {
        const container = document.querySelector('.what-if-machine-plan-summary-content-section-container');
        if (!element || !container) return { x: 0, y: 0 };

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        return {
            x: elementRect.left - containerRect.left + (elementRect.width / 2),
            y: elementRect.top - containerRect.top + (elementRect.height / 2)
        };
    }, []);

    const nodeTypes = useMemo(() => ({
        riskNode: RiskNode,
        projectNode: ProjectNode,
        houseMarkerNode: HouseMarkerNode
    }), []);


    const getNodes = () => {
        const nodes = [];

        // Risk nodes
        climateRisks?.forEach(risk => {
            const element = document.getElementById(`risk-${risk.risk_id}`);
            if (element) {
                const position = getElementPosition(element);
                // Subtract 60 from the y position
                position.y -= 60;
                nodes.push({
                    id: `risk-${risk.risk_id}`,
                    type: 'riskNode',
                    position,
                    visible: true,
                    hidden: false
                });
            }
        });

        // Project nodes
        projectsList?.forEach(project => {
            const element = document.getElementById(`project-${project.project_id}`);
            if (element) {
                const position = getElementPosition(element);
                // Subtract 70 from the y position
                position.y -= 20;
                nodes.push({
                    id: `project-${project.project_id}`,
                    type: 'projectNode',
                    position,
                    visible: true,
                    hidden: false
                });
            }
        });


        const projectHomeCategoryIds = new Set(projectsList.map(project => project.project_home_category_id));
        const filteredAreas = houseAreasThreatened.filter(area => projectHomeCategoryIds.has(area.id));
        // console.log('filteredAreas FROM THE USEEFFECT', filteredAreas);
        // House marker nodes
        filteredAreas?.forEach(area => {
            const element = document.getElementById(`area-${area.id}`);
            // console.log('Element when searching for', area, element);
            if (element) {
                const position = getElementPosition(element);

                // Subtract 90 from the y position
                // position.y -= 90;
                // console.log('Element when searching for', area, element, position);
                nodes.push({
                    id: `area-${area.id}`,
                    type: 'houseMarkerNode',
                    position,
                    visible: true,
                    hidden: false
                });
            }
        });
        // console.log('Nodes', nodes);
        setNodes(nodes);
        const edges = [];
        projectsList?.forEach(project => {
            // Risk to project connections
            project.project_associated_climate_risks?.forEach(riskName => {
                const risk = climateRisks?.find(r => r.risk_name === riskName.risk_name);
                let lineColor = 'var(--related_red)';
                let lineWidth = 3;
                let offset = 3;
                if (risk.risk_level === 'Medium') {
                    lineColor = 'var(--yellow)';
                    lineWidth = 2;
                    offset = 2;
                } else if (risk.risk_level === 'low') {
                    lineColor = 'green';
                    lineWidth = 1;
                    offset = 1;
                }
                if (risk) {
                    edges.push({
                        id: `edge-risk${risk.risk_id}-project${project.project_id}`,
                        source: `risk-${risk.risk_id}`,
                        target: `project-${project.project_id}`,
                        type: 'bezier',
                        pathOptions: {
                            offset: offset
                        },
                        animated: true,
                        style: {
                            stroke: lineColor,
                            strokeWidth: 2,
                            strokeLinecap: 'round',
                            animation: "dashdraw 0.5s linear infinite", // Adjust the duration here
                            strokeDasharray: 5
                        }
                    });
                }
            });

            // Project to house area connections
            edges.push({
                id: `edge-project${project.project_id}-area${project.project_home_category_id}`,
                source: `project-${project.project_id}`,
                target: `area-${project.project_home_category_id}`,
                type: 'bezier',
                animated: true,
                style: {
                    stroke: 'var(--dark-grey)',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                    animation: "dashdraw 0.5s linear infinite", // Adjust the duration here
                    strokeDasharray: 5
                }
            });
        });
        // console.log('Edges', edges);
        setEdges(edges);

        return { nodes, edges };
        // return nodes;
    };

    useEffect(() => {
        if (showFlow) {
            // Set 1 second delay to ensure the nodes and edges are set
            setTimeout(() => {
                getNodes();
            }, 100);
        }
    }, [showFlow, width, height]);

    const getEdges = useCallback(() => {
        const edges = [];
        projectsList?.forEach(project => {
            // Risk to project connections
            project.project_associated_climate_risks?.forEach(riskName => {
                const risk = climateRisks?.find(r => r.risk_name === riskName.risk_name);
                if (risk) {
                    edges.push({
                        id: `edge-risk${risk.risk_id}-project${project.project_id}`,
                        source: `risk-${risk.risk_id}`,
                        target: `project-${project.project_id}`,
                        type: 'bezier'
                    });
                }
            });

            // Project to house area connections
            edges.push({
                id: `edge-project${project.project_id}-area${project.project_home_category_id}`,
                source: `project-${project.project_id}`,
                target: `area-${project.project_home_category_id}`,
                type: 'bezier',

            });
        });
        // console.log('Edges', edges);
        return edges;
    }, [climateRisks, projectsList]);

    const getAreaClass = (id) => {
        const areaMap = {
            '2': 'roof',
            '1': 'landscaping',
            '6': 'windows',
            '10': 'hvac',
            '7': 'doors',
            '14': 'frame',
            '4': 'foundation',
            '9': 'siding',
            '5': 'plumbing',
            '13': 'basement'
        };
        return areaMap[id] || 'unknown';
    };

    // Tiny component that signals when DOM is ready
    const FlowTrigger = ({ onMount }) => {
        useEffect(() => {
            onMount();
        }, []);
        return <div style={{ display: 'none' }} />;
    };

    const edgeTypes = useMemo(() => ({
        smoothstep: SmoothStepEdge,
        bezier: BezierEdge
    }), []);

    useEffect(() => {
        if (showFlow && nodes.length > 0 && edges.length > 0 && !hasRunRef.current) {
            hasRunRef.current = true;
            const timer = setTimeout(() => {
                setEdges(prev => [...prev]);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [showFlow, nodes, edges]);

    // Filter the climcateRisks only those where an edge.source is equal to risk.risk_id
    // console.log('Filtered Climate Risks + edges + sortedProjectsList + filteredHouseAreasThreatened', filteredClimateRisks, edges, sortedProjectsList, filteredHouseAreasThreatened);

    return (
        <div className="what-if-machine-plan-summary-content-section-container">
            {/* <h3 className="section-subtitle">Parts of Home Threatened</h3> */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: '600px', position: 'relative', gap: '4rem' }}>
                {/* Risk Icons Row */}
                <div className="what-if-machine-plan-summary-climate-risk-icons">
                    {filteredClimateRisks?.map(risk => {
                        const edge = edges.find(edge => edge.source === `risk-${risk.risk_id}`);
                        // console.log('EDGE', edge);
                        return (
                            <div className={`what-if-machine-plan-summary-climate-risk-item ${edge ? '' : 'opaque'}`} key={risk.risk_id} id={`risk-${risk.risk_id}`}>
                                <RiskOverviewClimateIcon
                                    id={risk.risk_id}
                                    risk_level={risk.risk_level}
                                    icon_white={risk.icon_white}
                                    icon_black={risk.icon_black}
                                    risk_name={risk.risk_name}
                                    requiresLookup={false}
                                />
                            </div>
                        )
                    })}
                </div>

                {/* Projects Row */}
                <div className="what-if-machine-plan-summary-solving-for-projects-container">
                    {sortedProjectsList?.map(project => {

                        let showDatapoint = currentSection !== 0;
                        let datapointValue = '';
                        let datapointCSSClass = '';

                        let showWhoDoesWorkDatapoint = false;
                        let showWhoDoesWorkDatapointValue = '';


                        let showProjectColoredDataCircle = false;
                        let projectColoredDataCircleColor = '';

                        // Payoff section
                        if (currentSection === 1) {

                            // Get the projectsBreakdown from the simulatorData
                            const projectsBreakdown = simulatorData?.projectsBreakdown || [];

                            // Get the object from the projectsBreakdown array that has the project_id that matches the current project
                            const projectBreakdown = projectsBreakdown.find(breakdownProject => breakdownProject.project_id === project.project_id) || {};
                            // console.log("Project Breakdown", projectBreakdown);
                            // Get the value for the percentage of total payoff
                            const percentageOfTotalPayoff = projectBreakdown?.percentages?.total_payoff || 0;

                            // Multiply by 100 and format into text with no extra decimals
                            const formattedPercentageOfTotalPayoff = (percentageOfTotalPayoff * 100).toFixed(0);

                            // Get the value from the projectBreakdown object
                            datapointValue = `${formattedPercentageOfTotalPayoff >= 1 ? formattedPercentageOfTotalPayoff : '<1'}%`;
                            // console.log("Project ID and % of Value", project.project_id, formattedPercentageOfTotalPayoff);
                            datapointCSSClass = 'payoff';
                        } else if (currentSection === 2) {
                            // Get the value from the projectBreakdown object
                            datapointValue = project.project_discounts_company_specific?.length || 0;
                            datapointCSSClass = 'discounts';
                            showDatapoint = datapointValue > 0;
                        } else if (currentSection === 3) {
                            // Get the projectsBreakdown from the simulatorData
                            const projectsBreakdown = simulatorData?.projectsBreakdown || [];

                            // Get the object from the projectsBreakdown array that has the project_id that matches the current project
                            const projectBreakdown = projectsBreakdown.find(breakdownProject => breakdownProject.project_id === project.project_id) || {};

                            // Get the total_cost from projectBreakdown.percentages
                            const total_cost = projectBreakdown?.percentages?.total_cost || 0;

                            // Create a formatted % string from the total_cost
                            const formattedTotalCost = (total_cost * 100).toFixed(0);

                            // Get the value from the projectBreakdown object
                            datapointValue = `${formattedTotalCost >= 1 ? formattedTotalCost : '<1'}%`;
                            datapointCSSClass = 'costs';
                        }

                        // if (currentSection === 0 || currentSection === 3) { 
                        if (currentSection !== -1) {
                            // Get the project diyPreference from the project object
                            const diyPreference = project.diyPreference || null;
                            if (diyPreference) {
                                if (diyPreference === 'pro' || diyPreference === 'diy') {
                                    showWhoDoesWorkDatapoint = true;
                                    showWhoDoesWorkDatapointValue = diyPreference === 'pro' ? 'Hiring a Pro' : 'DIY';
                                }
                            }
                        }

                        if (currentSection === 3) {

                            // Get the project colorWheel from the project object
                            const colorWheel = project.colorWheel || null;
                            if (colorWheel) {
                                projectColoredDataCircleColor = colorWheel;
                                showProjectColoredDataCircle = true;
                            }
                        }


                        return (
                            <div className="what-if-machine-plan-summary-solving-for-projects-item" key={project.project_id} id={`project-${project.project_id}`}>
                                {showDatapoint && (
                                    <div className={`what-if-machine-plan-summary-solving-for-project-datapoint ${datapointCSSClass}`}>
                                        <span>{datapointValue}</span>
                                    </div>
                                )}
                                <ProjectsListCard
                                    project={project}
                                    handleProjectClick={() => { }}
                                    handleAddingProjectToSimulator={() => { }}
                                    handleRemovingProjectFromSimulator={() => { }}
                                    isSelected={false}
                                    simulatorView={false}
                                    showAllDetails={false}
                                />
                                {showWhoDoesWorkDatapoint && (
                                    <div className={`what-if-machine-plan-summary-solving-for-project-diy-datapoint`}>
                                        <span>{showWhoDoesWorkDatapointValue}</span>
                                        {showProjectColoredDataCircle && (
                                            <div className={`what-if-machine-plan-summary-solving-for-project-colored-data-circle`} style={{ backgroundColor: projectColoredDataCircleColor }}></div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* House Section */}
                <div className="house-diagram-section">
                    <div className="climate-factors-home-hub-container">
                        <img
                            className="climate-factors-home-hub-image"
                            src={new_hub_house_image_url}
                            alt="House diagram"
                        />
                        <div className="house-risk-markers combinations-lab">
                            {filteredHouseAreasThreatened?.map((area) => (
                                <div
                                    key={area.id}
                                    id={`area-${area.id}`}
                                    className={`house-risk-marker house-risk-marker-${getAreaClass(area.id)} ${area.risk_level_adjusted} combinations-lab`}
                                >
                                    <span>{area.category}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Marker div that triggers ReactFlow */}
                {filteredHouseAreasThreatened.length > 0 &&
                    <FlowTrigger onMount={() => setShowFlow(true)} />
                }

                {/* Only render ReactFlow after marker div exists */}
                {(showFlow && nodes.length > 0 && edges.length > 0 && filteredHouseAreasThreatened.length > 0) && (

                    <div className="what-if-machine-plan-summary-solving-for-container-flow-container">
                        {[null].map((_, index) => {
                            // console.log('NODES AND EDGES', nodes, edges);
                            return (
                                null
                            )
                        })}
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            fitView={false}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            zoomOnScroll={false}
                            panOnScroll={false}
                            preventScrolling={true}
                            edgeTypes={edgeTypes}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                zIndex: 1
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SolvingFor;